import React, { useEffect, useRef, useState } from "react";
import './TestimonialsSection.css';
import BackgroundDecoration from '../../components/BackgroundDecoration/BackgroundDecoration';
import AudioPlayer from '../../components/AudioPlayer/AudioPlayer';

/**
 * TestimonialsSection - Sección para mostrar testimonios y prueba social
 * Muestra reseñas de clientes con información del autor
 */
const TestimonialsSection = ({ title, subtitle, testimonials = [], theme = 'dark', backgroundDecoration }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const containerRef = useRef(null);
  const audioPlayerRef = useRef(null);

  // Auto-advance carousel
  useEffect(() => {
    if (testimonials.length === 0 || isPaused) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [testimonials.length, isPaused]);

  // Guard index
  useEffect(() => {
    if (testimonials.length === 0) {
      setCurrentIndex(0);
      return;
    }
    if (currentIndex >= testimonials.length) {
      setCurrentIndex(0);
    }
  }, [testimonials, currentIndex]);

  // Stop audio when section scrolls out of view
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          audioPlayerRef.current?.pause();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Stop audio when switching testimonial
  const handleIndexChange = (index) => {
    audioPlayerRef.current?.pause();
    setCurrentIndex(index);
  };

  const currentTestimonial = testimonials[currentIndex];

  const renderWithInstagramLinks = (text) => {
    const parts = text.split(/(@[\w.]+)/g);
    return parts.map((part, i) =>
      /^@[\w.]+$/.test(part) ? (
        <a
          key={i}
          href={`https://instagram.com/${part.slice(1)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="instagramLink"
        >
          {part}
        </a>
      ) : part
    );
  };

  return (
    <div ref={containerRef} className={`testimonialsContainer testimonialsContainer--${theme}`}>
      {backgroundDecoration && <BackgroundDecoration {...backgroundDecoration} />}
      {title && <h2 className="testimonialsTitle">{title.toUpperCase()}</h2>}
      {subtitle && <p className="testimonialsSubtitle">{subtitle}</p>}

      {currentTestimonial && (
        <div
          className="testimonialsShowcase"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <article className="testimonialSpotlight" key={currentIndex}>
            <div className="quoteIcon">"</div>
            <p className="testimonialText">{currentTestimonial.text}</p>

            {currentTestimonial.audio && (
              <AudioPlayer
                ref={audioPlayerRef}
                src={currentTestimonial.audio}
                theme={theme}
              />
            )}

            <div className="authorInfo">
              {currentTestimonial.image && (
                <img
                  src={currentTestimonial.image}
                  alt={currentTestimonial.author}
                  className="authorImage"
                />
              )}
              <div className="authorDetails">
                <p className="authorName">{currentTestimonial.author}</p>
                {currentTestimonial.role && (
                  <p className="authorRole">{currentTestimonial.role}</p>
                )}
                {currentTestimonial.company && (
                  <p className="authorCompany">{renderWithInstagramLinks(currentTestimonial.company)}</p>
                )}
              </div>
            </div>

            {currentTestimonial.rating && (
              <div className="rating">
                {[...Array(5)].map((_, i) => (
                  <span
                    key={i}
                    className={`star ${i < currentTestimonial.rating ? 'filled' : ''}`}
                  >
                    ★
                  </span>
                ))}
              </div>
            )}
          </article>
        </div>
      )}

      {testimonials.length > 1 && (
        <div className="testimonialsAuthorsNav">
          {testimonials.map((testimonial, index) => (
            <button
              key={index}
              type="button"
              className={`authorNavChip ${index === currentIndex ? 'active' : ''}`}
              onClick={() => handleIndexChange(index)}
            >
              {testimonial.author}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default TestimonialsSection;
